// Библиотека утверждённых ответов. Полноширинное представление: колонки списка у него нет —
// у формулировки нет «карточки», все её поля помещаются в самой строке, и пустая правая
// колонка рядом была бы поверхностью, которая ничего не означает.
//
// ## Зачем этот экран вообще существует
//
// Единственный источник исходящего текста помощника — эта библиотека. Модель ничего не
// сочиняет: она выбирает утверждённую формулировку по коду шага разговора, и сторож
// исходящего проверяет это второй раз перед отправкой. Без сказанной вслух фразы экран
// выглядит складом заготовок, и непонятно, почему его вообще надо пополнять.
//
// ## Два человека — это не бюрократия
//
// Заводит формулировку рекрутёр (право вести переписку), утверждает другой человек (право
// настраивать помощника), и утвердить собственный черновик сервер не даёт. Фразу, которую
// компания скажет живому соискателю, читают двое — это вся защита, которая осталась после
// того, как текст перестали сочинять на ходу.
//
// Строки сгруппированы по шагу разговора, а не свалены в один список: формулировка живёт на
// шаге, и вопрос «чем помощник спрашивает про вахту» — это вопрос про одну группу.
//
// ## Одно действие снаружи, редкие — под «Ещё»
//
// 13.09.2026 заказчик написал, что процесс сложный и «надо проще», а 14.09 — что в найме не
// нашёл, чем править формулировки. Здесь эти две жалобы сошлись в одном месте: в строке стояли
// ЧЕТЫРЕ кнопки одного веса — «Изменить», «Убрать», «Утвердить», «Вывести из обращения», —
// групп семнадцать, и формулировок в каждой несколько. Сотня одинаковых серых кнопок на экран,
// из которых он ищет одну.
//
// Снаружи осталось одно частое действие — «Изменить», ровно то, которого он не нашёл. Остальные
// три живут под «Ещё», и меню взято ГОТОВЫМ (`modules/settings/МенюДействий`), своего здесь не
// заводится: порядок пунктов, черта перед опасным и красный цвет обязаны быть одни на весь
// продукт — рука запоминает место, а не читает заново. То же меню стоит в списке людей и в
// очереди приёмки.
//
// ⚠️ Спрятать — не то же самое, что убрать, и разница здесь стоила обеих жалоб. Под меню ушли
// все три действия ЦЕЛИКОМ, включая те, на которые у человека нет права: право по-прежнему не
// снимает пункт, а объясняет отказ по нажатию (правило 10). Достижимость каждого пункта под
// рекрутёром и под администратором держит прогон, а не чтение кода.

import { useState } from 'react';

import { RiAddLine, RiBookOpenLine } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { МенюДействий } from '@/modules/settings/МенюДействий';
import type { ApiError } from '@/shared/errors/api-error';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateDMY, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';
import { Toolbar } from '@/shared/ui/toolbar';

import type { LibraryEntry, LibraryStatus, ScriptStep } from '../api/dialogs';
import { GateNote, useCan } from '../components/gates';
import { LIBRARY_STATUS_TITLES, PHRASE_FORMS } from '../dictionaries';
import { phraseKindTitle } from '../panels/NewPhrasePanel';
import {
  useAccountsQuery,
  useApprovePhraseMutation,
  useLibraryQuery,
  useDeletePhraseMutation,
  useEditPhraseMutation,
  useRetirePhraseMutation,
  useScriptQuery,
} from '../queries';
import type { HiringRoute } from '../route';
import {
  BLANK,
  FILTER_ACCOUNT,
  FILTER_STATUS,
  FILTER_STEP,
  PANEL_NEW_PHRASE,
  hiringHref,
  openPanelHref,
  setFilter,
} from '../route';

/** Утверждать и выводить формулировки — то же право, что и на режим помощника. */
const CONFIGURE = 'hiring.assistant.configure';

const LIBRARY_STATUSES = ['draft', 'approved', 'retired'] as const;

/** Оттенок состояния. Название приходит с сервера полем `status_title`. */
const СОСТОЯНИЕ_ЦВЕТ: Readonly<Record<LibraryStatus, 'orange' | 'green' | 'gray'>> = {
  draft: 'orange',
  approved: 'green',
  retired: 'gray',
};

const РАЗ_FORMS = ['раз', 'раза', 'раз'] as const;

export interface LibraryViewProps {
  route: HiringRoute;
}

export function LibraryView({ route }: LibraryViewProps) {
  const navigate = useNavigate();
  const { view, search } = route;

  const шагОтбора = search.filters[FILTER_STEP];
  const состояние = search.filters[FILTER_STATUS];
  const запись = search.filters[FILTER_ACCOUNT];

  const query = useLibraryQuery({
    step_code: шагОтбора,
    status: состояние,
    account_id: запись,
  });
  const шаги = useScriptQuery(запись);
  const записи = useAccountsQuery();

  const утвердить = useApprovePhraseMutation();
  const вывести = useRetirePhraseMutation();
  const изменить = useEditPhraseMutation();
  const убрать = useDeletePhraseMutation();

  /** Какую формулировку сейчас правят и что в поле. Одна за раз: две открытые дают рябь. */
  const [правим, setПравим] = useState<{ id: string; текст: string } | null>(null);
  /** У какой спросили «точно убрать?». Подтверждение живёт в строке, а не отдельным окном:
      формулировка это текст, а не деньги, и окно поверх экрана здесь дороже пользы. */
  const [убираем, setУбираем] = useState<string | null>(null);

  const разрешено = useCan(CONFIGURE);
  const [заметка, setЗаметка] = useState<{ id: string; текст: string } | null>(null);

  // `replace`: подбор отбора — один шаг в истории браузера, а не двадцать.
  function apply(next: SearchState) {
    void navigate(hiringHref(view, next), { replace: true });
  }

  function открытьМастер(код: string) {
    void navigate(openPanelHref(route, PANEL_NEW_PHRASE, код));
  }

  function решить(действие: typeof утвердить | typeof вывести, entryId: string, чего: string) {
    if (!разрешено) {
      setЗаметка({
        id: entryId,
        текст: `${чего} может тот, кто настраивает помощника, — обычно администратор. Это отдельное право, оно не совпадает с правом заводить формулировки.`,
      });
      return;
    }
    if (утвердить.isPending || вывести.isPending) {
      setЗаметка({
        id: entryId,
        текст:
          'Решение по формулировке уже уходит на сервер, ждём ответ. Второй раз не отправляем.',
      });
      return;
    }
    setЗаметка(null);
    действие.mutate(entryId);
  }

  const списокШагов = шаги.data?.items ?? [];
  const отборНаложен = шагОтбора !== undefined || состояние !== undefined || запись !== undefined;
  // Пока список пуст, кнопка в шапке не рисуется: то же самое действие теми же словами уже
  // предлагает пустое состояние, и там оно объяснено — почему библиотека нужна и что будет
  // дальше. Две одинаковые кнопки на одном экране заставляют искать между ними разницу,
  // которой нет. Так же разведено в ведомостях («Создать ведомости на период»), и поймал
  // это тот же сторож — он краснеет на повторе подписи.
  const естьФормулировки = (query.data?.items.length ?? 0) > 0;

  return (
    <div className='flex h-full min-w-0 flex-1 flex-col'>
      <Toolbar
        actions={
          естьФормулировки ? (
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='xsmall'
              // Идентификатор мастера — код выбранного шага: человек, отобравший «Готовность на
              // вахту», заводит формулировку именно на нём и не выбирает шаг второй раз.
              onClick={() => открытьМастер(шагОтбора ?? BLANK)}
            >
              <Button.Icon as={RiAddLine} />
              Новая формулировка
            </Button.Root>
          ) : undefined
        }
      >
        <div className='min-w-56'>
          <SelectField
            id='library-step'
            label='Шаг разговора'
            value={шагОтбора ?? NO_VALUE}
            onChange={(значение) =>
              apply(setFilter(search, FILTER_STEP, значение === NO_VALUE ? undefined : значение))
            }
            options={списокШагов.map((шаг) => ({ value: шаг.code, label: шаг.title }))}
            noneLabel='Все шаги'
            placeholder='Все шаги'
            pending={шаги.isPending}
            failed={шаги.isError}
            failedError={шаги.error as ApiError | null}
            emptyNote='Сценарий разговора пуст: шагов нет, а формулировка живёт на шаге.'
          />
        </div>

        <div className='min-w-56'>
          <SelectField
            id='library-status'
            label='Состояние'
            value={состояние ?? NO_VALUE}
            onChange={(значение) =>
              apply(setFilter(search, FILTER_STATUS, значение === NO_VALUE ? undefined : значение))
            }
            options={LIBRARY_STATUSES.map((код) => ({
              value: код,
              label: LIBRARY_STATUS_TITLES[код],
            }))}
            noneLabel='Любое состояние'
            placeholder='Любое состояние'
          />
        </div>

        <div className='min-w-56'>
          <SelectField
            id='library-account'
            label='Учётная запись площадки'
            value={запись ?? NO_VALUE}
            onChange={(значение) =>
              apply(setFilter(search, FILTER_ACCOUNT, значение === NO_VALUE ? undefined : значение))
            }
            options={(записи.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.title,
            }))}
            noneLabel='Все записи'
            placeholder='Все записи'
            pending={записи.isPending}
            failed={записи.isError}
            failedError={записи.error as ApiError | null}
            emptyNote='Учётных записей площадки нет: все формулировки общие.'
          />
        </div>
      </Toolbar>

      <div className='min-h-0 flex-1 overflow-y-auto p-5'>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <h1 className='text-label-md text-text-strong-950'>
              {query.data === undefined
                ? 'Ответы помощника'
                : countLabel(query.data.total, PHRASE_FORMS)}
            </h1>
            <p className='text-paragraph-sm text-text-sub-600'>
              Помощник не сочиняет текст: соискателю уходит только утверждённая формулировка отсюда,
              выбранная по шагу разговора. Ничего сверх библиотеки он сказать не может.
            </p>
            <p className='text-paragraph-xs text-text-sub-600'>
              Заводит формулировку рекрутёр и всегда черновиком, утверждает её другой человек — свой
              черновик утвердить нельзя. Вывод из обращения запись не удаляет: она остаётся, на неё
              ссылается журнал переписки, просто помощник её больше не отправляет.
            </p>
          </div>

          <ScreenState
            data={query.data}
            error={query.error}
            subject='библиотека ответов помощника'
            isEmpty={(данные) => данные.items.length === 0}
            onRetry={() => void query.refetch()}
            loading={
              <div className='flex flex-col gap-3'>
                <Skeleton className='h-32 w-full' />
                <Skeleton className='h-32 w-full' />
              </div>
            }
            empty={
              <EmptyState
                icon={RiBookOpenLine}
                title={
                  отборНаложен ? 'Под этот отбор формулировок нет' : 'Библиотека ответов пуста'
                }
                illustration={отборНаложен ? undefined : 'заметки'}
                description={
                  отборНаложен
                    ? 'Список пуст из-за наложенного отбора, а не потому, что библиотека пуста. Снимите шаг, состояние и учётную запись — и увидите всё, что заведено.'
                    : 'Пока в библиотеке нет ни одной формулировки, помощник в режиме «отвечает сам» не отправит соискателю ничего: текст он берёт только отсюда. Заведите первую — утвердит её другой человек.'
                }
                action={
                  отборНаложен ? undefined : (
                    <Button.Root
                      type='button'
                      variant='primary'
                      mode='filled'
                      size='small'
                      onClick={() => открытьМастер(шагОтбора ?? BLANK)}
                    >
                      <Button.Icon as={RiAddLine} />
                      Новая формулировка
                    </Button.Root>
                  )
                }
              />
            }
          >
            {(данные) => (
              <div className='flex flex-col gap-3'>
                {/* Заведённые, но не утверждённые — это тишина помощника, а не готовая
                    библиотека. Говорится один раз сверху, а не под каждым черновиком. */}
                {состояние === undefined &&
                  данные.items.every((строка) => строка.status !== 'approved') && (
                    <Hint.Root>
                      Утверждённых формулировок нет ни одной: всё, что заведено, ждёт второго
                      человека. Пока их не утвердят, помощник не отправит соискателю ничего.
                    </Hint.Root>
                  )}

                {/* Группы шагов — СПИСОК, и размечены списком.
                    Это не украшение семантики: кнопка «Формулировка» стоит у каждой группы, и
                    таких групп семнадцать. Вне списка семнадцать одинаковых подписей читаются
                    как семнадцать разных действий — и чтением с экрана, и сквозной проверкой
                    «одинаковых кнопок на экране не бывает», которая до 11.09.2026 была на этом
                    экране красной. Внутри списка это одно действие над каждым его пунктом,
                    ровно как «Принять» у каждой заявки в очереди приёмки. */}
                <ul className='flex flex-col gap-4'>
                  {погруппам(данные.items, списокШагов).map((группа) => (
                    <li key={группа.код}>
                      <Block
                        key={группа.код}
                        title={группа.название}
                        note={countLabel(группа.строки.length, PHRASE_FORMS)}
                        action={
                          группа.вСценарии ? (
                            <Button.Root
                              type='button'
                              variant='neutral'
                              mode='stroke'
                              size='xsmall'
                              onClick={() => открытьМастер(группа.код)}
                            >
                              <Button.Icon as={RiAddLine} />
                              Формулировка
                            </Button.Root>
                          ) : undefined
                        }
                      >
                        <ol className='flex flex-col'>
                          {группа.строки.map((строка) => (
                            <li
                              key={строка.id}
                              className='flex flex-col gap-1.5 border-b border-stroke-soft-200 py-3 last:border-b-0'
                            >
                              <div className='flex items-start justify-between gap-3'>
                                {правим !== null && правим.id === строка.id ? (
                                  /* Правка идёт ЗДЕСЬ ЖЕ, в строке, а не в панели сбоку:
                                     человек сравнивает новый текст с соседними формулировками
                                     того же шага, и увести его с экрана значит отнять эту
                                     возможность. */
                                  <div className='flex min-w-0 flex-1 flex-col gap-2'>
                                    <TextArea
                                      id={`library-edit-${строка.id}`}
                                      label='Текст формулировки'
                                      value={правим.текст}
                                      onChange={(значение) =>
                                        setПравим({ id: строка.id, текст: значение })
                                      }
                                      rows={3}
                                      hint='Это слова, которые помощник отправит соискателю.'
                                    />
                                    <div className='flex flex-wrap items-center gap-2'>
                                      <Button.Root
                                        type='button'
                                        variant='primary'
                                        mode='filled'
                                        size='xxsmall'
                                        onClick={() => {
                                          setЗаметка(null);
                                          изменить.mutate(
                                            { entryId: строка.id, text: правим.текст },
                                            { onSuccess: () => setПравим(null) },
                                          );
                                        }}
                                      >
                                        {изменить.isPending ? 'Сохраняем…' : 'Сохранить'}
                                      </Button.Root>
                                      <Button.Root
                                        type='button'
                                        variant='neutral'
                                        mode='ghost'
                                        size='xxsmall'
                                        onClick={() => setПравим(null)}
                                      >
                                        Отмена
                                      </Button.Root>
                                    </div>
                                  </div>
                                ) : (
                                  /* Текст формулировки пишет ЗАКАЗЧИК, а не продукт: это его
                                     слова, которые помощник отправит соискателю. Грамматику
                                     чисел в них мы не правим и не имеем права править — «80
                                     000-90 000р» это его вилка зарплаты, а не наш формат
                                     денег. Пометка говорит об этом сквозной проверке. */
                                  <p
                                    data-текст='от человека'
                                    className='min-w-0 flex-1 whitespace-pre-wrap text-paragraph-sm text-text-strong-950'
                                  >
                                    {строка.text}
                                  </p>
                                )}
                                <Badge.Root
                                  variant='lighter'
                                  color={СОСТОЯНИЕ_ЦВЕТ[строка.status] ?? 'gray'}
                                  size='medium'
                                  className='shrink-0'
                                >
                                  {строка.status_title}
                                </Badge.Root>
                              </div>

                              <p className='text-paragraph-xs text-text-sub-600'>
                                {происхождение(строка)}
                              </p>

                              {/* Кнопки прячутся ТОЛЬКО у той строки, которую сейчас
                                  правят: у неё вместо них поле и «Сохранить». У соседних они
                                  остаются — пропав со всего экрана разом, они читаются как
                                  «программа сломалась», а не как «идёт правка». */}
                              {(правим === null || правим.id !== строка.id) && (
                                <div className='flex flex-wrap items-center gap-2'>
                                  {/* Единственное частое действие, и потому единственное
                                      снаружи. Правка есть у формулировки в ЛЮБОМ состоянии,
                                      включая выведенную: её возвращают в работу заведением
                                      новой, и прочитать её текст без опечатки всё равно
                                      нужно. */}
                                  <Button.Root
                                    type='button'
                                    variant='neutral'
                                    mode='stroke'
                                    size='xxsmall'
                                    onClick={() => {
                                      setЗаметка(null);
                                      setУбираем(null);
                                      setПравим({ id: строка.id, текст: строка.text });
                                    }}
                                  >
                                    Изменить
                                  </Button.Root>

                                  {убираем === строка.id ? (
                                    /* Вопрос стоит ВМЕСТО меню и написан словами. Раньше его
                                       задавала сама кнопка «Убрать», на месте которой вырастал
                                       ответ; из меню она исчезает вместе с ним, и два ответа
                                       появились бы в строке без единого вопроса на экране.
                                       Утверждать и выводить, пока вопрос висит, нечего. */
                                    <>
                                      <span className='text-paragraph-xs text-text-sub-600'>
                                        Убрать формулировку насовсем?
                                      </span>
                                      <Button.Root
                                        type='button'
                                        variant='error'
                                        mode='filled'
                                        size='xxsmall'
                                        onClick={() => {
                                          setЗаметка(null);
                                          убрать.mutate(строка.id, {
                                            onSuccess: () => setУбираем(null),
                                          });
                                        }}
                                      >
                                        {убрать.isPending ? 'Убираем…' : 'Да, убрать'}
                                      </Button.Root>
                                      <Button.Root
                                        type='button'
                                        variant='neutral'
                                        mode='ghost'
                                        size='xxsmall'
                                        onClick={() => setУбираем(null)}
                                      >
                                        Отмена
                                      </Button.Root>
                                    </>
                                  ) : (
                                    /* Редкие решения. Пункты СЛОВАМИ и без значков: «карандаш»
                                       у правки и «карандаш» у заметки не различаются вовсе.
                                       Состояние решает, какие пункты есть: утверждают только
                                       черновик, выводят только то, что ещё в обращении. Права
                                       пункт НЕ снимают — отказ объясняется по нажатию, иначе
                                       человек ищет кнопку, которой для него не нарисовали. */
                                    <МенюДействий
                                      подпись={началоФразы(строка.text)}
                                      пункты={[
                                        ...(строка.status === 'draft'
                                          ? [
                                              {
                                                подпись: 'Утвердить',
                                                выполнить: () =>
                                                  решить(
                                                    утвердить,
                                                    строка.id,
                                                    'Утверждать формулировки',
                                                  ),
                                              },
                                            ]
                                          : []),
                                        ...(строка.status !== 'retired'
                                          ? [
                                              {
                                                подпись: 'Вывести из обращения',
                                                выполнить: () =>
                                                  решить(
                                                    вывести,
                                                    строка.id,
                                                    'Выводить формулировки из обращения',
                                                  ),
                                              },
                                            ]
                                          : []),
                                        {
                                          // Опасное, красным и за чертой: убрать насовсем можно
                                          // только то, чего помощник ни разу не говорил.
                                          // Отправленную сервер не отдаст — на неё ссылается
                                          // переписка с соискателем.
                                          подпись: 'Убрать',
                                          опасное: true,
                                          выполнить: () => {
                                            setЗаметка(null);
                                            setУбираем(строка.id);
                                          },
                                        },
                                      ]}
                                    />
                                  )}

                                  {/* Отклик на выбор из меню. Пункт, по которому ударили,
                                      исчезает вместе с меню, и занятое состояние вешать не на
                                      что: у кнопки оно было написано прямо на ней. Без этой
                                      строки человек смотрит на неизменившуюся строку и жмёт
                                      «Утвердить» второй раз — то самое молчание, ради которого
                                      заведено правило 11. */}
                                  {утвердить.isPending && утвердить.variables === строка.id && (
                                    <span className='text-paragraph-xs text-text-sub-600'>
                                      Утверждаем…
                                    </span>
                                  )}
                                  {вывести.isPending && вывести.variables === строка.id && (
                                    <span className='text-paragraph-xs text-text-sub-600'>
                                      Выводим…
                                    </span>
                                  )}
                                </div>
                              )}

                              {заметка !== null && заметка.id === строка.id && (
                                <GateNote>{заметка.текст}</GateNote>
                              )}
                              {утвердить.isError && утвердить.variables === строка.id && (
                                <ErrorAlert error={утвердить.error} />
                              )}
                              {вывести.isError && вывести.variables === строка.id && (
                                <ErrorAlert error={вывести.error} />
                              )}
                              {изменить.isError && изменить.variables?.entryId === строка.id && (
                                <ErrorAlert error={изменить.error} />
                              )}
                              {убрать.isError && убрать.variables === строка.id && (
                                <ErrorAlert error={убрать.error} />
                              )}
                            </li>
                          ))}
                        </ol>
                      </Block>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </ScreenState>
        </div>
      </div>
    </div>
  );
}

interface Группа {
  код: string;
  название: string;
  /** Шаг есть в сценарии: только на такой можно завести новую формулировку. */
  вСценарии: boolean;
  строки: LibraryEntry[];
}

/**
 * Разложить формулировки по шагам разговора в порядке самого разговора.
 *
 * Шаги, которых в сценарии уже нет, уходят вниз и остаются видимыми: их формулировки
 * заведены, на них ссылается журнал переписки, и молчаливо спрятать их значило бы потерять
 * половину библиотеки после первой же правки сценария.
 */
function погруппам(строки: readonly LibraryEntry[], шаги: readonly ScriptStep[]): Группа[] {
  const порядок = new Map<string, number>();
  const названия = new Map<string, string>();
  for (const шаг of шаги) {
    порядок.set(шаг.code, шаг.order_no);
    названия.set(шаг.code, шаг.title);
  }

  const собрано = new Map<string, LibraryEntry[]>();
  for (const строка of строки) {
    const группа = собрано.get(строка.step_code);
    if (группа === undefined) {
      собрано.set(строка.step_code, [строка]);
    } else {
      группа.push(строка);
    }
  }

  const группы: Группа[] = [];
  for (const [код, содержимое] of собрано) {
    const название = названия.get(код);
    группы.push({
      код,
      название: название ?? `Шаг вне сценария: ${код}`,
      вСценарии: название !== undefined,
      строки: содержимое,
    });
  }

  const ВНИЗ = Number.MAX_SAFE_INTEGER;
  группы.sort((левая, правая) => {
    const слева = порядок.get(левая.код) ?? ВНИЗ;
    const справа = порядок.get(правая.код) ?? ВНИЗ;
    return слева !== справа ? слева - справа : левая.код.localeCompare(правая.код, 'ru');
  });
  return группы;
}

/**
 * Сколько букв формулировки влезает в шапку меню, не раздувая его.
 *
 * Меню шириной в 52 единицы сетки: полсотни знаков — это две строки мелким шрифтом, и дальше
 * пункты уезжают из-под курсора вниз.
 */
const ШАПКА_МЕНЮ = 48;

/**
 * Начало формулировки для шапки меню: «по какой из них я сейчас решаю».
 *
 * Целиком её туда класть нельзя — это абзац, который помощник отправляет соискателю, а не
 * имя строки. Режем по границе слова: обрубок посреди слова читается как сбой программы, а не
 * как начало фразы. Переводы строк схлопываются в пробел по той же причине.
 */
function началоФразы(текст: string): string {
  const одной = текст.replace(/\s+/g, ' ').trim();
  if (одной.length <= ШАПКА_МЕНЮ) {
    return одной;
  }
  const обрез = одной.slice(0, ШАПКА_МЕНЮ);
  const пробел = обрез.lastIndexOf(' ');
  return `${пробел > 0 ? обрез.slice(0, пробел) : обрез}…`;
}

/** Откуда формулировка взялась и как часто её говорят. Ноль и «не записано» — не одно и то же. */
function происхождение(запись: LibraryEntry): string {
  const части: string[] = [phraseKindTitle(запись.kind)];

  части.push(
    запись.author_name !== null && запись.author_name !== ''
      ? `завёл ${запись.author_name}`
      : 'кто завёл — не записано',
  );

  if (запись.approved_by_name !== null && запись.approved_by_name !== '') {
    const момент = parseMoment(запись.approved_at);
    части.push(
      момент === null
        ? `утвердил ${запись.approved_by_name}`
        : `утвердил ${запись.approved_by_name}, ${formatDateDMY(момент)}`,
    );
  } else if (запись.status === 'draft') {
    части.push('ещё не утверждена');
  }

  части.push(
    запись.usage_count === 0
      ? 'ни разу не отправлялась'
      : `отправлена ${countLabel(запись.usage_count, РАЗ_FORMS)}`,
  );

  if (запись.account_id === null) {
    части.push('общая для всех записей');
  }

  return части.join(' · ');
}
